import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import { submitPartnerRequest } from "../api/apiFunction/partnerServices";
import { updatePageTitle } from "../utils/titleUtils";
import { LogoWithText } from "../components/Logo";
import { Building2, User, Briefcase, GraduationCap, Globe, Heart, Rocket, Mail, Phone, CheckCircle2, Sparkles, TrendingUp, Users, Award, ArrowLeft } from "lucide-react";
import * as Sentry from "@sentry/react";

const { logger } = Sentry;

const PartnerRequest = () => {
  const [formData, setFormData] = useState({
    name: "",
    type: "university",
    email: "",
    contactInfo: "",
    organization: "",
    reason: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    updatePageTitle("Become a Partner");
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validation
    if (!formData.name || !formData.email || !formData.type) {
      setError("Name, email, and partner type are required");
      setLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      const response = await submitPartnerRequest(formData);
      
      // Check if response is successful (status 200-299)
      if (response && (response.status === 201 || response.status === 200)) {
        // Show success toast
        toast.success("Partner request submitted successfully! We'll review your application and get back to you via email within 2-3 business days.", {
          position: "bottom-right",
          autoClose: 4000,
        });
        
        // Redirect to login page after a short delay
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        // Handle API error response - user stays on page
        const errorMessage = response?.data?.message || response?.data?.error || "Failed to submit partner request. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "bottom-right",
          autoClose: 5000,
        });
        setLoading(false);
      }
    } catch (error) {
      // Handle network or other errors - user stays on page
      Sentry.captureException(error);
      logger.error("Partner request error", { error: error.message });
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || "An error occurred while submitting your request. Please check your connection and try again.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 5000,
      });
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "university":
        return GraduationCap;
      case "company":
        return Building2;
      case "startup":
        return Rocket;
      case "ngo":
        return Heart;
      case "government":
        return Globe;
      default:
        return Briefcase;
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-bg-50 via-bg-60 to-bg-50 flex items-center justify-center p-4">
        <div className="w-full max-w-xl">
          <div className="bg-bg-70 rounded-2xl shadow-xl p-6 lg:p-8 text-center space-y-5">
            {/* Success Icon */}
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={2.5} />
            </div>
            
            {/* Success Message */}
            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-fg-50">Request Submitted!</h2>
              <p className="text-sm text-fg-60 leading-relaxed">
                Thank you for your interest. We've received your request and will review it shortly.
              </p>
              <div className="bg-bg-60 rounded-lg p-3 border border-bd-50">
                <p className="text-xs text-fg-60 mb-1">We'll send updates to:</p>
                <p className="text-sm text-fg-50 font-medium flex items-center justify-center gap-1.5">
                  <Mail size={14} />
                  {formData.email}
                </p>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-gradient-to-r from-ac-02/10 to-ac-01/10 rounded-lg p-4 border border-ac-02/20 text-left">
              <h3 className="text-xs font-semibold text-fg-50 mb-2 flex items-center gap-1.5">
                <Sparkles size={14} className="text-ac-02" />
                What happens next?
              </h3>
              <ul className="text-xs text-fg-60 space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-ac-02 mt-0.5">✓</span>
                  <span>Review within 2-3 business days</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-ac-02 mt-0.5">✓</span>
                  <span>Email notification when decision is made</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-ac-02 mt-0.5">✓</span>
                  <span>Access to partner dashboard if approved</span>
                </li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <button
                onClick={() => navigate("/")}
                className="flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold bg-ac-02 text-white dark:text-black hover:bg-ac-01 focus:outline-none focus:ring-2 focus:ring-ac-02 transition-all duration-200 shadow-md"
              >
                Return to Home
              </button>
              <Link
                to="/contact"
                className="flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold bg-bg-60 text-fg-50 hover:bg-bg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 transition-all duration-200 border border-bd-50 text-center"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-bg-50 via-bg-60 to-bg-50 flex items-center justify-center p-4">
      {/* Form Container */}
      <div className="w-full max-w-2xl">
        <div className="bg-bg-70 rounded-2xl shadow-xl p-6 lg:p-8">
          {/* Back Button */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-fg-60 hover:text-fg-50 mb-4 transition-colors group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Home</span>
          </button>

          <div className="w-full space-y-5">
            {/* Logo and Header */}
            <div className="text-center space-y-2">
              <div className="flex justify-center mb-2">
                <LogoWithText className="h-8 w-auto" />
              </div>
              <h1 className="text-2xl lg:text-3xl font-bold text-fg-50">
                Become a Partner
              </h1>
              <p className="text-sm text-fg-60">
                Join our partner program and unlock exclusive benefits
              </p>
            </div>

            {/* Benefits Preview */}
            <div className="grid grid-cols-3 gap-2 p-3 bg-gradient-to-r from-ac-02/5 to-ac-01/5 rounded-lg border border-ac-02/10">
              <div className="text-center">
                <TrendingUp className="w-5 h-5 text-ac-02 mx-auto mb-1" />
                <p className="text-[10px] font-medium text-fg-50">Earn Rewards</p>
              </div>
              <div className="text-center">
                <Users className="w-5 h-5 text-ac-02 mx-auto mb-1" />
                <p className="text-[10px] font-medium text-fg-50">Grow Network</p>
              </div>
              <div className="text-center">
                <Award className="w-5 h-5 text-ac-02 mx-auto mb-1" />
                <p className="text-[10px] font-medium text-fg-50">Exclusive Access</p>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700 rounded-xl p-4 flex items-start gap-3 animate-shake">
                <div className="flex-shrink-0 mt-0.5">
                  <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-sm text-red-700 dark:text-red-300 flex-1">{error}</p>
              </div>
            )}

            {/* Form */}
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-xs font-semibold text-fg-50 mb-1.5 flex items-center gap-1.5">
                  <User size={14} className="text-fg-60" />
                  Name / Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-3 py-2.5 bg-bg-60 border border-bd-50 rounded-lg text-sm text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent transition-all duration-200"
                  placeholder="Enter your name or organization"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-semibold text-fg-50 mb-1.5 flex items-center gap-1.5">
                  <Mail size={14} className="text-fg-60" />
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-3 py-2.5 bg-bg-60 border border-bd-50 rounded-lg text-sm text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent transition-all duration-200"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-fg-50 mb-2">
                  Partner Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {["university", "company", "startup", "ngo", "government", "individual", "organization"].map((type) => {
                    const Icon = getTypeIcon(type);
                    return (
                      <label
                        key={type}
                        className={`group flex items-center gap-2 p-2.5 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                          formData.type === type
                            ? "border-ac-02 bg-gradient-to-br from-ac-02/20 to-ac-01/10 shadow-md"
                            : "border-bd-50 bg-bg-60 hover:border-ac-02/50 hover:bg-bg-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="type"
                          value={type}
                          checked={formData.type === type}
                          onChange={handleChange}
                          className="hidden"
                          disabled={loading}
                        />
                        <Icon 
                          size={18} 
                          className={`flex-shrink-0 transition-colors ${
                            formData.type === type ? "text-ac-02" : "text-fg-60 group-hover:text-ac-02"
                          }`} 
                        />
                        <span className={`text-xs font-medium capitalize transition-colors ${
                          formData.type === type ? "text-fg-50" : "text-fg-60"
                        }`}>
                          {type}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label htmlFor="contactInfo" className="block text-xs font-semibold text-fg-50 mb-1.5 flex items-center gap-1.5">
                  <Phone size={14} className="text-fg-60" />
                  Contact Information
                </label>
                <input
                  id="contactInfo"
                  name="contactInfo"
                  type="text"
                  className="w-full px-3 py-2.5 bg-bg-60 border border-bd-50 rounded-lg text-sm text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent transition-all duration-200"
                  placeholder="Phone number (optional)"
                  value={formData.contactInfo}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="organization" className="block text-xs font-semibold text-fg-50 mb-1.5 flex items-center gap-1.5">
                  <Building2 size={14} className="text-fg-60" />
                  Organization (if applicable)
                </label>
                <input
                  id="organization"
                  name="organization"
                  type="text"
                  className="w-full px-3 py-2.5 bg-bg-60 border border-bd-50 rounded-lg text-sm text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent transition-all duration-200"
                  placeholder="Organization name (optional)"
                  value={formData.organization}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="reason" className="block text-xs font-semibold text-fg-50 mb-1.5">
                  Why do you want to become a partner?
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  rows={4}
                  className="w-full px-3 py-2.5 bg-bg-60 border border-bd-50 rounded-lg text-sm text-fg-50 placeholder-fg-70 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="Tell us about your organization and why you'd like to partner with us..."
                  value={formData.reason}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="pt-1">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold text-white dark:text-black transition-all duration-200 shadow-md ${
                    loading
                      ? "bg-ac-02 bg-opacity-50 cursor-not-allowed"
                      : "bg-gradient-to-r from-ac-02 to-ac-01 hover:from-ac-01 hover:to-ac-02 hover:shadow-lg hover:scale-[1.01] active:scale-[0.99]"
                  } focus:outline-none focus:ring-2 focus:ring-ac-02/50`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      <span>Submit Request</span>
                    </>
                  )}
                </button>
                <p className="text-[10px] text-fg-70 text-center mt-2">
                  By submitting, you agree to our{" "}
                  <Link to="/terms" className="text-ac-02 hover:text-ac-01 underline">Terms</Link>
                  {" "}and{" "}
                  <Link to="/privacy-policy" className="text-ac-02 hover:text-ac-01 underline">Privacy Policy</Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerRequest;

